import type React from "react";
import { SyntheticEvent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Chip, FormControlLabel, MenuItem, Select, SelectChangeEvent, Slider, Stack, useTheme } from "@mui/material";
import { Family } from "types";
import { Label } from "components/Label";
import type { IFamilyFilters } from "../types";
import getStyles from "./styles";

type FiltersProps = {
  colorArr: number[];
  fluxArr: number[];
  opticsArr: string[];
  familyFilters: IFamilyFilters;
  selectedFamilyIds: string[];
  families?: Family[];
  addFilter: (key: keyof IFamilyFilters, value: number[] | string[]) => void;
  onDeleteChip: (familyId: string) => void;
};

const Filters: React.FC<FiltersProps> = ({ colorArr, fluxArr, opticsArr, familyFilters, addFilter, selectedFamilyIds, families, onDeleteChip }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Filters, [theme]);

  const handleColorChange = useCallback((event: SelectChangeEvent<number[]>) => {
    addFilter("color", event.target.value as number[]);
  }, [addFilter]);

  const handleFluxChange = useCallback((_: Event, value: number | number[]) => {
    addFilter("flux", value as number[]);
  }, [addFilter]);

  const handleOpticChange = useCallback((event: SyntheticEvent<Element, Event>, checked: boolean) => {
    const value = (event.target as HTMLInputElement).value;
    const newOptics = checked ? [...familyFilters.optic, value] : familyFilters.optic.filter((optic) => optic !== value);
    addFilter("optic", newOptics);
  }, [addFilter, familyFilters.optic]);

  const selectedFamilies = useMemo(() => selectedFamilyIds.map((id) => families?.find((family) => family.id === id)).filter(Boolean), [families, selectedFamilyIds]);

  return (
    <Stack sx={styles.container}>
      {colorArr.length > 1 && (
        <Stack sx={styles.group}>
          <Label text={t("Color temperature")} sx={styles.label} />
          <Select
            multiple
            value={familyFilters.color}
            onChange={handleColorChange}
            renderValue={(selected) => selected.map((e) => `${e}K`).join(", ")}
            sx={styles.select}
          >
            {colorArr.map((color) => (
              <MenuItem key={color} value={color}>
                <Checkbox checked={familyFilters.color.includes(color)} />
                {`${color}K`}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      {fluxArr.length > 1 && (
        <Stack sx={styles.group}>
          <Label text={t("Luminous flux")} sx={styles.label} />
          <Slider
            max={Math.max(...fluxArr)}
            min={Math.min(...fluxArr)}
            value={familyFilters.flux}
            valueLabelFormat={(value) => `${value} lm`}
            valueLabelDisplay="auto"
            onChange={handleFluxChange}
            sx={styles.slider}
          />
        </Stack>
      )}

      {opticsArr.length > 0 && (
        <Stack sx={styles.group}>
          <Label text={t("Light distribution")} sx={styles.label} />
          <Stack sx={styles.chekcboxes}>
            {opticsArr.map((optic) => (
              <FormControlLabel
                label={optic}
                key={optic}
                checked={familyFilters.optic.includes(optic)}
                onChange={handleOpticChange}
                labelPlacement="start"
                control={<Checkbox value={optic} />}
                sx={{ ml: 0 }}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {selectedFamilyIds.length > 0 && (
        <Stack sx={styles.group}>
          <Label text={t("Selected families")} sx={styles.label} />
          <Stack sx={styles.chips}>
            {selectedFamilies.map((family) => {
              if (!family) return null;
              return <Chip key={family.id} label={family.name} onDelete={() => onDeleteChip(family.id)} />;
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default Filters;
